/**
 * Firebase configuration exports
 * Direct configuration for Firebase services
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getAuth, Auth, setPersistence, browserLocalPersistence, onAuthStateChanged, signOut } from 'firebase/auth';

// Log environment variable status for debugging
console.log('[FIREBASE CONFIG] Environment variables status:', {
  apiKey: import.meta.env.VITE_PRESENTATION_FIREBASE_API_KEY ? 'defined' : 'undefined',
  projectId: import.meta.env.VITE_PRESENTATION_FIREBASE_PROJECT_ID ? 'defined' : 'undefined'
});

// Firebase configuration for the presentation app
// Using hardcoded values to ensure consistency between local and deployed environments
const firebaseConfig = {
  apiKey: "AIzaSyCAaE1EFm1BHvLXB7UJ7rmjP1QXM70Ss4k",
  authDomain: "jimi-wellness-presentation.firebaseapp.com",
  projectId: "jimi-wellness-presentation",
  storageBucket: "jimi-wellness-presentation.firebasestorage.app",
  messagingSenderId: "856341610593",
  appId: "1:856341610593:web:5c8e989b4198a9f98eaa20",
  measurementId: "G-5Y26EGK0JX"
};

// Initialize variables
let app: FirebaseApp;
let presentationDb: Firestore;
let presentationStorage: FirebaseStorage;
let presentationAuth: Auth;

try {
  // Initialize Firebase
  console.log('[FIREBASE CONFIG] Initializing with config:', {
    projectId: firebaseConfig.projectId,
    apiKeyLength: firebaseConfig.apiKey ? firebaseConfig.apiKey.length : 0
  });
  
  app = initializeApp(firebaseConfig, 'presentation');

  // Initialize services
  presentationDb = getFirestore(app);
  presentationStorage = getStorage(app);
  presentationAuth = getAuth(app);

  // Set persistence to LOCAL to keep the user logged in
  // This ensures the user stays logged in even after browser refresh
  setPersistence(presentationAuth, browserLocalPersistence)
    .then(() => {
      console.log('[FIREBASE CONFIG] Persistence set to LOCAL');
      
      // After setting persistence, check if we have a user
      const currentUser = presentationAuth.currentUser;
      if (currentUser) {
        console.log('[FIREBASE CONFIG] User already authenticated:', currentUser.email);
      } else {
        console.log('[FIREBASE CONFIG] No user currently authenticated');
        
        // Check if we have stored authentication in localStorage
        const storedEmail = localStorage.getItem('admin_email');
        const storedAuth = localStorage.getItem('admin_authenticated');
        
        if (storedEmail && storedAuth === 'true') {
          console.log('[FIREBASE CONFIG] Found stored auth credentials, user should be authenticated soon');
        }
      }
    })
    .catch((error) => {
      console.error('[FIREBASE CONFIG] Error setting persistence:', error);
    });

  // Set up an auth state listener to debug auth state changes
  onAuthStateChanged(presentationAuth, (user) => {
    if (user) {
      console.log('[FIREBASE CONFIG] Auth state changed: User is signed in', user.email);
    } else {
      console.log('[FIREBASE CONFIG] Auth state changed: User is signed out');
    }
  });

  // Configure Storage with custom settings
  // This helps with retry issues in environments with connectivity challenges
  presentationStorage.maxOperationRetryTime = 30000; // 30 seconds max retry time
  presentationStorage.maxUploadRetryTime = 60000;    // 60 seconds max upload retry time

  console.log(`[FIREBASE CONFIG] Services initialized for project: ${app.options.projectId}`);
} catch (error) {
  console.error('[FIREBASE CONFIG] Initialization error:', error);
  throw new Error('Failed to initialize Firebase. Check your API keys and configuration.');
}

// Helper function to clear authentication data from localStorage
export const clearAuthData = () => {
  console.log('[FIREBASE CONFIG] Clearing authentication data from localStorage');
  localStorage.removeItem('admin_email');
  localStorage.removeItem('admin_authenticated');
  localStorage.removeItem('elevenLabsApiKey');
};

// Helper function to sign out user completely
export const signOutCompletely = async () => {
  try {
    console.log('[FIREBASE CONFIG] Signing out user completely');
    
    // Clear localStorage data first
    clearAuthData();
    
    // Sign out from Firebase
    await presentationAuth.signOut();
    
    console.log('[FIREBASE CONFIG] User signed out successfully');
    return true;
  } catch (error) {
    console.error('[FIREBASE CONFIG] Error signing out:', error);
    return false;
  }
};

// Export services
export { presentationDb, presentationStorage, presentationAuth };

// Export services with standardized names
export const firestore = presentationDb;
export const storage = presentationStorage;
export const auth = presentationAuth;

// Additional standardized names
export const db = firestore;
export const storageRef = storage;
